import { ColumnDef } from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import { ArrowUpDown, ArrowUp, ArrowDown, ExternalLink } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { type DatasourceBill } from '@/api/datasource-bills-api'
import { type DatasourceSortField, type DatasourceSortDirection } from '@/hooks/bills/useDatasourceBills'

interface GetDatasourceBillsColumnsProps {
  sortField?: DatasourceSortField
  sortDirection?: DatasourceSortDirection
  onSortChange?: (field: DatasourceSortField, direction: DatasourceSortDirection) => void
}

export function getDatasourceBillsColumns({
  sortField,
  sortDirection,
  onSortChange
}: GetDatasourceBillsColumnsProps = {}): ColumnDef<DatasourceBill>[] {

  const createSortableHeader = (
    field: DatasourceSortField,
    label: string
  ) => {
    const isCurrentSort = sortField === field
    const currentDirection = isCurrentSort ? sortDirection : null

    const handleSort = () => {
      if (!onSortChange) return

      if (!isCurrentSort) {
        onSortChange(field, 'asc')
      } else {
        const newDirection = currentDirection === 'asc' ? 'desc' : 'asc'
        onSortChange(field, newDirection)
      }
    }

    const SortIcon = () => {
      if (!isCurrentSort) return <ArrowUpDown className="ml-2 h-4 w-4" />
      return currentDirection === 'asc' 
        ? <ArrowUp className="ml-2 h-4 w-4" />
        : <ArrowDown className="ml-2 h-4 w-4" />
    }

    return (
      <Button
        variant="ghost"
        onClick={handleSort}
        className="h-auto p-0 font-semibold hover:bg-transparent"
      >
        {label}
        <SortIcon />
      </Button>
    )
  }

  return [
    {
      accessorKey: "bill_number",
      header: () => createSortableHeader('bill_number', 'Bill Number'),
      cell: ({ row }) => {
        const bill = row.original
        return (
          <div className="font-mono font-medium">
            {bill.bill_number}
          </div>
        )
      },
    },
    {
      accessorKey: "title",
      header: () => createSortableHeader('title', 'Title'),
      cell: ({ row }) => {
        const bill = row.original
        return (
          <div className="max-w-[300px]">
            <div className="font-medium truncate" title={bill.title}>
              {bill.title}
            </div>
            {bill.synopsis && (
              <div className="text-sm text-muted-foreground line-clamp-2 mt-1" title={bill.synopsis}>
                {bill.synopsis}
              </div>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: "session_code",
      header: "Session",
      cell: ({ row }) => {
        const bill = row.original
        return (
          <Badge variant="outline" className="font-mono">
            {bill.session_code}
          </Badge>
        )
      },
    },
    {
      accessorKey: "crossfile_bill_number",
      header: "Cross-file",
      cell: ({ row }) => {
        const crossfile = row.original.crossfile_bill_number
        return crossfile ? (
          <span className="font-mono text-sm">{crossfile}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      accessorKey: "data_source",
      header: "Data Source",
      cell: ({ row }) => {
        const bill = row.original
        return (
          <Badge variant="secondary">
            {bill.data_source}
          </Badge>
        )
      },
    },
    {
      accessorKey: "versions",
      header: "Versions",
      cell: ({ row }) => {
        const versions = row.original.versions || []
        return (
          <div className="text-center">
            <Badge variant="outline">
              {versions.length}
            </Badge>
          </div>
        )
      },
    },
    {
      accessorKey: "full_text_available",
      header: "Full Text",
      cell: ({ row }) => {
        const available = row.original.full_text_available
        return (
          <Badge variant={available ? "default" : "destructive"}>
            {available ? "Available" : "Unavailable"}
          </Badge>
        )
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const bill = row.original
        
        return (
          <div className="flex items-center gap-2">
            {bill.pdf_url && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  window.open(bill.pdf_url, '_blank', 'noopener,noreferrer')
                }}
                title="View PDF"
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
          </div>
        )
      },
    },
  ]
}
